/**
 * trajectory.ts — baked sword trajectories: the data the whole combat system
 * stands on (AGENTS.md: one transform sequence).
 *
 * A trajectory is the weapon bone's transform RELATIVE TO THE CHARACTER ROOT,
 * sampled at SUBSAMPLE_HZ across an attack clip's active window, produced offline
 * by `scripts/bakeTrajectories.mjs`. The server never runs skeletons: the sword's
 * world transform is the attacker's root transform composed with one of these
 * samples.
 *
 * The generated data lives in `./generated/trajectories.js`; this module holds
 * the types and the accessors everything else uses.
 */
import { FEEL } from './feel.js';
export const sampleCount = (traj) => traj.positions.length / 3;
/** Reads sample `i`. Throws on an out-of-range index rather than returning junk. */
export function readSample(traj, i) {
    const count = sampleCount(traj);
    if (!Number.isInteger(i) || i < 0 || i >= count) {
        throw new RangeError(`sample ${i} out of range for ${traj.clipId} (${count} samples)`);
    }
    const p = i * 3;
    const r = i * 4;
    return {
        pos: [traj.positions[p], traj.positions[p + 1], traj.positions[p + 2]],
        rot: [traj.rotations[r], traj.rotations[r + 1], traj.rotations[r + 2], traj.rotations[r + 3]],
    };
}
/** Rotates [x,y,z] by the quaternion [x,y,z,w]. */
export function rotateByQuat(v, q) {
    const [vx, vy, vz] = v;
    const [qx, qy, qz, qw] = q;
    // t = 2 * (q_vec × v); result = v + qw * t + q_vec × t
    const tx = 2 * (qy * vz - qz * vy);
    const ty = 2 * (qz * vx - qx * vz);
    const tz = 2 * (qx * vy - qy * vx);
    return [
        vx + qw * tx + (qy * tz - qz * ty),
        vy + qw * ty + (qz * tx - qx * tz),
        vz + qw * tz + (qx * ty - qy * tx),
    ];
}
/**
 * The sword capsule for one sample, in character-root space: hilt at the weapon
 * bone origin, tip SWORD_LENGTH along the bone's local -Z.
 */
export function swordCapsuleLocal(sample) {
    const [px, py, pz] = sample.pos;
    const blade = rotateByQuat([0, 0, -FEEL.SWORD_LENGTH], sample.rot);
    return {
        hilt: [px, py, pz],
        tip: [px + blade[0], py + blade[1], pz + blade[2]],
    };
}
/** Clip time of sample `i`, ms from the start of the clip. */
export const sampleTimeMs = (traj, i) => traj.startMs + (i * 1000) / traj.hz;
//# sourceMappingURL=trajectory.js.map